import { API_DOC_EXAMPLES, type ApiDocExampleItem } from './examples';

export type ApiDocRouteId =
    | 'postClients'
    | 'postLeads'
    | 'postProducts'
    | 'postWarehouseMove'
    | 'getClients'
    | 'getLeads'
    | 'getLeadEvents'
    | 'getObjects'
    | 'getObjectById'
    | 'getWarehouses'
    | 'getWarehouseStocks'
    | 'getWarehouseOperations';

export type ApiDocWebhookId =
    | 'client_create'
    | 'lead_create'
    | 'lead_event_create'
    | 'product_create'
    | 'warehouse_income'
    | 'object_create'
    | 'object_status_change'
    | 'object_events'
    | 'user_update'
    | 'user_delete'
    | 'object_delete';

/** Нормалізація path з API (object_id / product_id → camelCase) */
export const normalizeRoutePath = (path: string): string =>
    path.replace(/:object_id\b/g, ':objectId').replace(/:product_id\b/g, ':productId');

export const routeKey = (method: string, path: string): string =>
    `${method.toUpperCase()} ${normalizeRoutePath(path)}`;

export const ROUTE_ID_BY_KEY: Record<string, ApiDocRouteId> = {
    'POST /clients': 'postClients',
    'POST /leads': 'postLeads',
    'POST /products': 'postProducts',
    'POST /warehouses/move': 'postWarehouseMove',
    'GET /clients': 'getClients',
    'GET /leads': 'getLeads',
    'GET /leads/:leadId/events': 'getLeadEvents',
    'GET /objects': 'getObjects',
    'GET /objects/:objectId': 'getObjectById',
    'GET /warehouses': 'getWarehouses',
    'GET /warehouses/:warehouseId/stocks': 'getWarehouseStocks',
    'GET /warehouses/:warehouseId/operations/:productId': 'getWarehouseOperations',
};

export const WEBHOOK_IDS: ApiDocWebhookId[] = [
    'client_create',
    'lead_create',
    'lead_event_create',
    'product_create',
    'warehouse_income',
    'object_create',
    'object_status_change',
    'object_events',
    'user_update',
    'user_delete',
    'object_delete',
];

/** Поля запиту: ключ у таблиці = шлях у JSON; i18nKey — без крапок (для i18next) */
export type ApiDocFieldDef = { key: string; i18nKey: string };

export const ROUTE_FIELDS: Partial<Record<ApiDocRouteId, ApiDocFieldDef[]>> = {
    postClients: [
        { key: 'clientName', i18nKey: 'clientName' },
        { key: 'clientEmail', i18nKey: 'clientEmail' },
        { key: 'clientPhone', i18nKey: 'clientPhone' },
        { key: 'clientAddress', i18nKey: 'clientAddress' },
        { key: 'clientLocation', i18nKey: 'clientLocation' },
        { key: 'clientDescription', i18nKey: 'clientDescription' },
        { key: 'clientStatus', i18nKey: 'clientStatus' },
    ],
    postLeads: [
        { key: 'clientId', i18nKey: 'clientId' },
        { key: 'client', i18nKey: 'client' },
        { key: 'eventInfo', i18nKey: 'eventInfo' },
        { key: 'sourceLeadId', i18nKey: 'sourceLeadId' },
        { key: 'sourceLeadMessage', i18nKey: 'sourceLeadMessage' },
        { key: 'sourceLeadData', i18nKey: 'sourceLeadData' },
    ],
    postProducts: [
        { key: 'prodArticle', i18nKey: 'prodArticle' },
        { key: 'prodName', i18nKey: 'prodName' },
        { key: 'vendorId', i18nKey: 'vendorId' },
        { key: 'dimensionId', i18nKey: 'dimensionId' },
        { key: 'prodDescription', i18nKey: 'prodDescription' },
        { key: 'prodDefPrice', i18nKey: 'prodDefPrice' },
        { key: 'prodDefPriceSale', i18nKey: 'prodDefPriceSale' },
    ],
    postWarehouseMove: [
        { key: 'operationType', i18nKey: 'operationType' },
        { key: 'whFrom', i18nKey: 'whFrom' },
        { key: 'whTo', i18nKey: 'whTo' },
        { key: 'whIdentFrom', i18nKey: 'whIdentFrom' },
        { key: 'whIdentTo', i18nKey: 'whIdentTo' },
        { key: 'items', i18nKey: 'items' },
        { key: 'items[].productId', i18nKey: 'itemsProductId' },
        { key: 'items[].prodArticle', i18nKey: 'itemsProdArticle' },
        { key: 'items[].count', i18nKey: 'itemsCount' },
        { key: 'items[].price', i18nKey: 'itemsPrice' },
        { key: 'items[].priceRate', i18nKey: 'itemsPriceRate' },
        { key: 'items[].description', i18nKey: 'itemsDescription' },
        { key: 'payment', i18nKey: 'payment' },
        { key: 'payment.amount', i18nKey: 'paymentAmount' },
        { key: 'payment.amountRate', i18nKey: 'paymentAmountRate' },
        { key: 'payment.description', i18nKey: 'paymentDescription' },
    ],
};

export const ROUTE_QUERY_FIELDS: Partial<Record<ApiDocRouteId, ApiDocFieldDef[]>> = {
    getClients: [
        { key: 'page', i18nKey: 'page' },
        { key: 'onPage', i18nKey: 'onPage' },
        { key: 'search', i18nKey: 'search' },
        { key: 'order', i18nKey: 'order' },
        { key: 'status', i18nKey: 'status' },
    ],
    getLeads: [
        { key: 'page', i18nKey: 'page' },
        { key: 'onPage', i18nKey: 'onPage' },
        { key: 'search', i18nKey: 'search' },
        { key: 'order', i18nKey: 'order' },
        { key: 'status', i18nKey: 'status' },
    ],
    getObjects: [
        { key: 'page', i18nKey: 'page' },
        { key: 'onPage', i18nKey: 'onPage' },
        { key: 'search', i18nKey: 'search' },
        { key: 'order', i18nKey: 'order' },
        { key: 'status', i18nKey: 'status' },
        { key: 'teamId', i18nKey: 'teamId' },
    ],
    getWarehouseStocks: [
        { key: 'page', i18nKey: 'page' },
        { key: 'onPage', i18nKey: 'onPage' },
        { key: 'search', i18nKey: 'search' },
        { key: 'order', i18nKey: 'order' },
        { key: 'vendors', i18nKey: 'vendors' },
        { key: 'showAll', i18nKey: 'showAll' },
    ],
    getWarehouseOperations: [
        { key: 'page', i18nKey: 'page' },
        { key: 'onPage', i18nKey: 'onPage' },
    ],
};

export const ROUTE_PATH_FIELDS: Partial<Record<ApiDocRouteId, ApiDocFieldDef[]>> = {
    getLeadEvents: [{ key: 'leadId', i18nKey: 'leadId' }],
    getObjectById: [{ key: 'objectId', i18nKey: 'objectId' }],
    getWarehouseStocks: [{ key: 'warehouseId', i18nKey: 'warehouseId' }],
    getWarehouseOperations: [
        { key: 'warehouseId', i18nKey: 'warehouseId' },
        { key: 'productId', i18nKey: 'productId' },
    ],
};

export const ROUTE_EXAMPLES: Partial<Record<ApiDocRouteId, ApiDocExampleItem[]>> = {
    postClients: [
        {
            id: 'createClient',
            labelKey: 'apiDocs.examples.createClient.label',
            payload: { ...API_DOC_EXAMPLES.createClient },
        },
    ],
    postLeads: [
        {
            id: 'createLeadWithClient',
            labelKey: 'apiDocs.examples.createLeadWithClient.label',
            hintKey: 'apiDocs.examples.createLeadWithClient.hint',
            payload: { ...API_DOC_EXAMPLES.createLeadWithClient },
        },
        {
            id: 'createLeadWithClientId',
            labelKey: 'apiDocs.examples.createLeadWithClientId.label',
            hintKey: 'apiDocs.examples.createLeadWithClientId.hint',
            payload: { ...API_DOC_EXAMPLES.createLeadWithClientId },
        },
    ],
    postProducts: [
        {
            id: 'createProduct',
            labelKey: 'apiDocs.examples.createProduct.label',
            payload: { ...API_DOC_EXAMPLES.createProduct },
        },
    ],
    postWarehouseMove: [
        {
            id: 'comingArticle',
            labelKey: 'apiDocs.examples.comingArticle.label',
            hintKey: 'apiDocs.examples.comingArticle.hint',
            payload: { ...API_DOC_EXAMPLES.warehouseComingByArticle },
        },
        {
            id: 'comingProductId',
            labelKey: 'apiDocs.examples.comingProductId.label',
            hintKey: 'apiDocs.examples.comingProductId.hint',
            payload: { ...API_DOC_EXAMPLES.warehouseComingByProductId },
        },
        {
            id: 'transferArticle',
            labelKey: 'apiDocs.examples.transferArticle.label',
            hintKey: 'apiDocs.examples.transferArticle.hint',
            payload: { ...API_DOC_EXAMPLES.warehouseTransferByArticle },
        },
        {
            id: 'transferIdent',
            labelKey: 'apiDocs.examples.transferIdent.label',
            hintKey: 'apiDocs.examples.transferIdent.hint',
            payload: { ...API_DOC_EXAMPLES.warehouseTransferByIdent },
        },
        {
            id: 'transferPrice',
            labelKey: 'apiDocs.examples.transferPrice.label',
            hintKey: 'apiDocs.examples.transferPrice.hint',
            payload: { ...API_DOC_EXAMPLES.warehouseTransferByPrice },
        },
    ],
};

export const ROUTE_NOTES_KEYS: Partial<Record<ApiDocRouteId, string[]>> = {
    postClients: ['apiDocs.routes.postClients.notes.identity'],
    postLeads: ['apiDocs.routes.postLeads.notes.clientOrId', 'apiDocs.routes.postLeads.notes.event'],
    postProducts: ['apiDocs.routes.postProducts.notes.uniqueArticle'],
    postWarehouseMove: [
        'apiDocs.routes.postWarehouseMove.notes.productRef',
        'apiDocs.routes.postWarehouseMove.notes.comingPrice',
        'apiDocs.routes.postWarehouseMove.notes.transferRef',
        'apiDocs.routes.postWarehouseMove.notes.transferPrice',
        'apiDocs.routes.postWarehouseMove.notes.currency',
    ],
};
